import { useOutsideClick } from "@hooks";
import { Check, ChevronDown, Plus, X } from "@icons";
import classNames from "classnames";
import {
  ReactNode,
  Ref,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal, flushSync } from "react-dom";

import { FieldProps } from "../_shared/field";
import FieldLabel from "../_shared/field-label";
import FieldMessage from "../_shared/field-message";
import { useAnnouncer } from "./use-announcer";

export interface ComboboxOption {
  value: string;
  /** Text shown for the option, in the menu and (single-select) in the field. */
  label: string;
  /** Render the option greyed out and unselectable. */
  disabled?: boolean;
  /** Small badge shown on a disabled option (e.g. "Locked"). */
  hint?: string;
}

export interface ComboboxGroup {
  /** Section heading; omit for an ungrouped list. */
  group?: string;
  items: ComboboxOption[];
}

/** Overridable UI strings (English defaults; pass translated copy from the app). */
export interface ComboboxLabels {
  selectAll: string;
  deselectAll: string;
  clear: string;
  /** Menu toolbar count, e.g. `(n) => `${n} selected``. */
  selectedCount: (count: number) => string;
  /** Collapsed-chips pill, e.g. `(n) => `+${n} more``. */
  more: (count: number) => string;
  showLess: string;
  empty: string;
  searchPlaceholder: string;
  /** Create-row prefix; the typed query is appended in bold. */
  create: string;
  /** Fallback badge on a disabled option that has no `hint`. */
  unavailable: string;
  /** Shown in the field and menu while `loading`. */
  loading: string;
  clearAll: string;
  removeOption: (label: string) => string;
  optionRemoved: (label: string) => string;
  selectionCleared: string;
  resultsAvailable: (count: number) => string;
  createOptionName: (label: string) => string;
  created: (label: string) => string;
}

const DEFAULT_LABELS: ComboboxLabels = {
  selectAll: "Select all",
  deselectAll: "Deselect all",
  clear: "Clear",
  selectedCount: (count) => `${count} selected`,
  more: (count) => `+${count} more`,
  showLess: "Show less",
  empty: "No results",
  searchPlaceholder: "Search…",
  create: "Create",
  unavailable: "Unavailable",
  loading: "Loading…",
  clearAll: "Clear all selections",
  removeOption: (label) => `Remove ${label}`,
  optionRemoved: (label) => `Removed: ${label}`,
  selectionCleared: "Selection cleared",
  resultsAvailable: (count) =>
    count === 1 ? "1 result available" : `${count} results available`,
  createOptionName: (label) => `Create “${label}”, new entry`,
  created: (label) => `Created and selected: ${label}`,
};

/**
 * App-wide overrides for the built-in UI strings of every {@link Combobox} and
 * {@link Select}. Wrap the app in a provider supplying translated copy so the
 * controls localize without a `labels` prop at each call site; the per-instance
 * `labels` prop still wins over the context.
 */
export const ControlLabelsContext = createContext<Partial<ComboboxLabels>>({});

function Spinner({ className }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={classNames(
        "inline-block w-[15px] h-[15px] shrink-0 rounded-full border-2 border-divider border-t-primary animate-spin",
        className,
      )}
    />
  );
}

const CREATED_OPTION_GROUP = "Custom";
const CREATED_VALUE_PREFIX = "custom:";

/** Extra scroll so the active option isn't flush against the menu edge. */
const ACTIVE_OPTION_SCROLL_MARGIN = 4;

/** Popup sizing/placement (px). The popup is portaled and positioned against the field. */
const MENU_MAX_HEIGHT = 288;
const MENU_GAP = 6;
const MENU_VIEWPORT_MARGIN = 8;

const RESULT_COUNT_ANNOUNCE_DELAY_MS = 300;
const TYPEAHEAD_RESET_MS = 500;
const NO_ACTIVE_OPTION = -1;

const CHIP_OVERFLOW_BUTTON_CLASS =
  "relative hit-target inline-flex items-center h-[26px] px-2.5 rounded-full border border-dashed border-default bg-transparent text-xs font-medium text-muted cursor-pointer transition-colors hover:bg-surface hover:text-default hover:border-default";

/**
 * Suppress the default mousedown on menu buttons and options so the click
 * doesn't blur the search input and collapse the menu before it's handled.
 */
function keepSearchInputFocused(event: React.MouseEvent) {
  event.preventDefault();
}

/**
 * The nearest ancestor that establishes a containing block for `position: fixed`
 * (a transform/filter/perspective/will-change/contain), e.g. a centered Radix
 * dialog. The portaled menu is hosted here — and its fixed coordinates measured
 * relative to it — so it positions correctly and stays inside that element's
 * focus scope. Returns null when fixed resolves against the viewport.
 */
function findFixedContainingBlock(
  element: HTMLElement | null,
): HTMLElement | null {
  let node = element?.parentElement ?? null;
  while (node && node !== document.body) {
    const style = getComputedStyle(node);
    if (
      style.transform !== "none" ||
      style.perspective !== "none" ||
      style.filter !== "none" ||
      /transform|perspective|filter/.test(style.willChange) ||
      /paint|layout|strict|content/.test(style.contain)
    ) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

function indexOptionsByValue(
  options: ComboboxOption[],
): Record<string, ComboboxOption> {
  const optionByValue: Record<string, ComboboxOption> = {};
  options.forEach((option) => (optionByValue[option.value] = option));
  return optionByValue;
}

function ComboboxToolbar({
  allSelected,
  selectedCount,
  strings,
  onSelectAll,
  onClear,
}: {
  allSelected: boolean;
  selectedCount: number;
  strings: ComboboxLabels;
  onSelectAll: () => void;
  onClear: () => void;
}) {
  const buttonClass =
    "min-h-target min-w-target px-1.5 py-0.5 rounded border-0 bg-transparent text-xs font-medium text-primary cursor-pointer transition-colors hover:bg-tint";

  return (
    <div className="flex shrink-0 items-center justify-between px-2 py-0 border-b border-divider">
      <button
        type="button"
        className={buttonClass}
        onMouseDown={keepSearchInputFocused}
        onClick={allSelected ? onClear : onSelectAll}
      >
        {allSelected ? strings.deselectAll : strings.selectAll}
      </button>
      <span className="text-xs text-muted">
        {strings.selectedCount(selectedCount)}
      </span>
      <button
        type="button"
        className={classNames(
          buttonClass,
          "disabled:text-disabled disabled:hover:bg-transparent disabled:cursor-default",
        )}
        onMouseDown={keepSearchInputFocused}
        onClick={onClear}
        disabled={selectedCount === 0}
      >
        {strings.clear}
      </button>
    </div>
  );
}

function OptionMarker({
  multiple,
  selected,
}: {
  multiple: boolean;
  selected: boolean;
}) {
  if (multiple) {
    return (
      <span
        className={classNames(
          "grid place-items-center w-[17px] h-[17px] shrink-0 rounded border-[1.5px] text-on-primary transition-[background,border-color]",
          selected ? "bg-primary border-primary" : "border-default",
        )}
      >
        {selected && <Check size={12} strokeWidth={2.5} />}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center justify-center w-4 h-4 shrink-0 text-primary">
      {selected && <Check size={15} strokeWidth={2.25} />}
    </span>
  );
}

function ComboboxOptionRow({
  id,
  option,
  multiple,
  selected,
  active,
  navIndex,
  unavailableLabel,
  onActivate,
  onSelect,
}: {
  id?: string;
  option: ComboboxOption;
  multiple: boolean;
  selected: boolean;
  active: boolean;
  navIndex?: number;
  unavailableLabel: string;
  onActivate: (event: React.MouseEvent) => void;
  onSelect: () => void;
}) {
  return (
    <div
      id={id}
      data-nav={option.disabled ? undefined : navIndex}
      role="option"
      aria-selected={selected}
      aria-disabled={option.disabled || undefined}
      onMouseMove={(event) => {
        if (!option.disabled) onActivate(event);
      }}
      onMouseDown={keepSearchInputFocused}
      onClick={onSelect}
      className={classNames(
        "flex min-h-target items-center gap-2.5 p-2 rounded leading-tight text-default",
        option.disabled ? "opacity-45 cursor-not-allowed" : "cursor-pointer",
        selected && "font-medium text-default",
        active && "bg-surface",
      )}
    >
      <OptionMarker multiple={multiple} selected={selected} />
      <span className="flex-1 min-w-0">{option.label}</span>
      {option.disabled && (
        <span className="shrink-0 rounded-full border border-divider px-2 py-px text-[0.6875rem] text-muted">
          {option.hint ?? unavailableLabel}
        </span>
      )}
    </div>
  );
}

function ComboboxCreateRow({
  id,
  query,
  active,
  standalone,
  navIndex,
  prefix,
  accessibleName,
  onActivate,
  onCreate,
}: {
  id: string;
  query: string;
  active: boolean;
  standalone: boolean;
  navIndex: number;
  prefix: string;
  accessibleName: string;
  onActivate: (event: React.MouseEvent) => void;
  onCreate: () => void;
}) {
  return (
    <div
      id={id}
      data-nav={navIndex}
      role="option"
      aria-selected={false}
      aria-label={accessibleName}
      onMouseMove={onActivate}
      onMouseDown={keepSearchInputFocused}
      onClick={onCreate}
      className={classNames(
        "flex min-h-target items-center gap-2.5 p-2 rounded cursor-pointer font-medium text-primary",
        standalone && "mt-0.5",
        active && "bg-tint",
      )}
    >
      <Plus size={16} strokeWidth={1.75} />
      <span className="flex-1 min-w-0">
        {prefix} <span className="text-default">“{query}”</span>
      </span>
    </div>
  );
}

function ComboboxChips({
  options,
  maxChips,
  expanded,
  disabled,
  strings,
  onRemove,
  onExpand,
  onCollapse,
}: {
  options: ComboboxOption[];
  maxChips: number;
  expanded: boolean;
  disabled: boolean;
  strings: ComboboxLabels;
  onRemove: (value: string, index: number) => void;
  onExpand: () => void;
  onCollapse: () => void;
}) {
  const visibleOptions = expanded ? options : options.slice(0, maxChips);
  const hiddenCount = options.length - visibleOptions.length;

  return (
    <div
      data-combobox-chips=""
      className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-0.5"
    >
      {visibleOptions.map((option, chipIndex) => (
        <span
          key={option.value}
          className="my-[9px] inline-flex items-center gap-1.5 h-[26px] pl-2.5 pr-1 rounded-full bg-tint text-primary text-[0.8125rem] font-medium max-w-full"
        >
          <span className="overflow-hidden text-ellipsis whitespace-nowrap">
            {option.label}
          </span>
          {!disabled && (
            <button
              type="button"
              onClick={() => onRemove(option.value, chipIndex)}
              data-chip-remove=""
              aria-label={strings.removeOption(option.label)}
              title={strings.removeOption(option.label)}
              className="relative hit-target inline-flex items-center justify-center w-[18px] h-[18px] shrink-0 p-0 border-0 bg-transparent rounded-full text-primary cursor-pointer transition-colors hover:bg-tint hover:text-primary"
            >
              <X size={13} />
            </button>
          )}
        </span>
      ))}
      {hiddenCount > 0 && (
        <button
          type="button"
          onClick={onExpand}
          className={CHIP_OVERFLOW_BUTTON_CLASS}
        >
          {strings.more(hiddenCount)}
        </button>
      )}
      {expanded && options.length > maxChips && (
        <button
          type="button"
          onClick={onCollapse}
          className={CHIP_OVERFLOW_BUTTON_CLASS}
        >
          {strings.showLess}
        </button>
      )}
    </div>
  );
}

export interface ComboboxProps extends Omit<
  FieldProps,
  "valid" | "defaultBadge"
> {
  /** Flat option list; a convenience alternative to {@link groups}. */
  options?: ComboboxOption[];
  /** Grouped option list (takes precedence over {@link options}). */
  groups?: ComboboxGroup[];
  /** Selected values (controlled). Single-select carries at most one. */
  value: string[];
  /** Fires with the next full selection. */
  onChange: (value: string[]) => void;
  /** Multi-select: per-option checkboxes, a select-all toolbar and a chips panel. */
  multiple?: boolean;
  placeholder?: string;
  autoComplete?: string;
  /** Type-to-filter (default). When false the field is a plain click-to-open dropdown. */
  searchable?: boolean;
  /** Show a spinner in the field and menu, e.g. while options are being fetched. */
  loading?: boolean;
  /** Show the clear (×) affordance when there is a selection (default true). */
  clearable?: boolean;
  /**
   * Server-driven search: called with the query on each keystroke. Supplying it
   * switches off client-side filtering (the parent returns already-filtered options).
   */
  onSearchChange?: (query: string) => void;
  /** Offer a "Create …" row when the query matches no option. */
  allowCreate?: boolean;
  /** Called with the typed label when a create row is chosen. */
  onCreate?: (label: string) => void;
  /** Chips past this count collapse into a "+N more" toggle. */
  maxChips?: number;
  /** Valid state passthrough for the shared message row. */
  valid?: string | boolean;
  /** Always-on muted helper text below the field. */
  hint?: string;
  /** Actions rendered at the right end of the label row (e.g. a "manage" link). */
  labelActions?: ReactNode;
  /**
   * Keep the label as the input's accessible name but hide it visually — for
   * surfaces that already print the field's name next to the control.
   */
  labelHidden?: boolean;
  /** Overrides for the built-in UI strings. */
  labels?: Partial<ComboboxLabels>;
  /** Notified when the control loses focus (react-hook-form integration). */
  onBlur?: () => void;
  /** Ref to the search input — used by {@link ControlledCombobox} to wire react-hook-form's field ref. */
  inputRef?: Ref<HTMLInputElement>;
}

export function Combobox({
  options,
  groups,
  value,
  onChange,
  multiple = false,
  label,
  required = false,
  info,
  infoOpen = false,
  placeholder,
  autoComplete,
  searchable = true,
  loading = false,
  clearable = true,
  onSearchChange,
  allowCreate = false,
  onCreate,
  maxChips = 6,
  error,
  valid,
  hint,
  disabled = false,
  name,
  labelActions,
  labelHidden = false,
  labels,
  onBlur,
  inputRef,
}: ComboboxProps) {
  const contextLabels = useContext(ControlLabelsContext);
  const strings = { ...DEFAULT_LABELS, ...contextLabels, ...labels };

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [chipsExpanded, setChipsExpanded] = useState(false);
  const [createdOptions, setCreatedOptions] = useState<ComboboxOption[]>([]);
  const [menuPosition, setMenuPosition] = useState<{
    left: number;
    width: number;
    maxHeight: number;
    top?: number;
    bottom?: number;
  } | null>(null);
  const [portalHost, setPortalHost] = useState<HTMLElement | null>(null);

  const rootRef = useRef<HTMLDivElement | null>(null);
  const fieldRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const activeMovedRef = useRef(false);
  const activeActedRef = useRef(false);
  const lastPointerRef = useRef<{ x: number; y: number } | null>(null);
  const suppressOpenOnFocusRef = useRef(false);
  const typeaheadBufferRef = useRef("");
  const typeaheadTimerRef = useRef<number | undefined>(undefined);
  const { announcement, announce, retract } = useAnnouncer();
  const resultMessageRef = useRef<string | null>(null);

  const inputId = useId();
  const listboxId = `${inputId}-listbox`;

  const selectedValues = useMemo(() => new Set(value), [value]);
  const hasError = !!error;

  const availableGroups = useMemo<ComboboxGroup[]>(() => {
    const base = groups ?? (options ? [{ items: options }] : []);
    if (createdOptions.length === 0) return base;
    return [...base, { group: CREATED_OPTION_GROUP, items: createdOptions }];
  }, [groups, options, createdOptions]);

  const optionByValue = useMemo(
    () => indexOptionsByValue(availableGroups.flatMap((group) => group.items)),
    [availableGroups],
  );

  const normalizedQuery = query.trim().toLowerCase();
  const clientSideFilter = searchable && !onSearchChange;
  const matchingGroups = useMemo(() => {
    if (!clientSideFilter || !normalizedQuery) return availableGroups;
    return availableGroups
      .map((group) => ({
        ...group,
        items: group.items.filter((option) =>
          option.label.toLowerCase().includes(normalizedQuery),
        ),
      }))
      .filter((group) => group.items.length > 0);
  }, [availableGroups, normalizedQuery, clientSideFilter]);

  const navigableOptions = useMemo(
    () =>
      matchingGroups.flatMap((group) =>
        group.items.filter((option) => !option.disabled),
      ),
    [matchingGroups],
  );
  const navIndexByValue = useMemo(() => {
    const byValue: Record<string, number> = {};
    navigableOptions.forEach(
      (option, index) => (byValue[option.value] = index),
    );
    return byValue;
  }, [navigableOptions]);

  const queryMatchesExistingOption = Object.values(optionByValue).some(
    (option) => option.label.toLowerCase() === normalizedQuery,
  );
  const showCreateRow =
    allowCreate && normalizedQuery.length > 0 && !queryMatchesExistingOption;
  const createRowIndex = navigableOptions.length;
  const navigableCount = navigableOptions.length + (showCreateRow ? 1 : 0);
  const currentIndex = Math.min(activeIndex, navigableCount - 1);

  const selectedOptions = useMemo(
    () =>
      value.map(
        (selectedValue) =>
          optionByValue[selectedValue] ?? {
            value: selectedValue,
            label: selectedValue,
          },
      ),
    [value, optionByValue],
  );
  const singleSelectedOption = multiple ? undefined : selectedOptions[0];

  const selectableOptions = useMemo(
    () =>
      availableGroups.flatMap((group) =>
        group.items.filter((option) => !option.disabled),
      ),
    [availableGroups],
  );
  const allOptionsSelected =
    selectableOptions.length > 0 &&
    selectableOptions.every((option) => selectedValues.has(option.value));

  function assignRef(node: HTMLInputElement | null) {
    searchRef.current = node;
    if (typeof inputRef === "function") inputRef(node);
    else if (inputRef)
      (inputRef as React.MutableRefObject<HTMLInputElement | null>).current =
        node;
  }

  const latestRef = useRef({ query, onSearchChange });
  useLayoutEffect(() => {
    latestRef.current = { query, onSearchChange };
  });

  const resetNavigation = useCallback((index: number, moved = false) => {
    setActiveIndex(index);
    activeMovedRef.current = moved;
    activeActedRef.current = false;
    lastPointerRef.current = null;
  }, []);

  const clearQuery = useCallback(() => {
    if (latestRef.current.query) latestRef.current.onSearchChange?.("");
    setQuery("");
    resetNavigation(0);
  }, [resetNavigation]);

  const menuMounted =
    open && !disabled && menuPosition !== null && portalHost !== null;

  useEffect(() => () => window.clearTimeout(typeaheadTimerRef.current), []);

  const wasLoadingRef = useRef(loading);
  useEffect(() => {
    const finishedLoading = wasLoadingRef.current && !loading;
    wasLoadingRef.current = loading;
    if (finishedLoading && open && !activeMovedRef.current)
      setActiveIndex(navIndexByValue[value[0]] ?? 0);
  }, [loading, open, navIndexByValue, value]);

  const resultMessage =
    navigableCount === 0
      ? strings.empty
      : strings.resultsAvailable(navigableCount);

  useEffect(() => {
    if (open) announce("");
  }, [open, announce]);

  useEffect(() => {
    if (!open || loading || !normalizedQuery) {
      if (resultMessageRef.current) retract(resultMessageRef.current);
      resultMessageRef.current = null;
      return;
    }
    const timer = window.setTimeout(() => {
      resultMessageRef.current = resultMessage;
      announce(resultMessage);
    }, RESULT_COUNT_ANNOUNCE_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [open, loading, normalizedQuery, resultMessage, announce, retract]);

  useEffect(() => {
    if (!open) return;
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key !== "Escape" || event.isComposing || event.keyCode === 229)
        return;
      if (event.target !== searchRef.current) return;
      event.preventDefault();
      event.stopPropagation();
      if (latestRef.current.query) clearQuery();
      else setOpen(false);
    }
    window.addEventListener("keydown", closeOnEscape, true);
    return () => window.removeEventListener("keydown", closeOnEscape, true);
  }, [open, clearQuery]);

  useEffect(() => {
    if (!menuMounted || !listRef.current) return;
    const list = listRef.current;
    const activeOption = list.querySelector<HTMLElement>(
      `[data-nav="${currentIndex}"]`,
    );
    if (!activeOption) return;
    const optionTop = activeOption.offsetTop;
    const optionBottom = optionTop + activeOption.offsetHeight;
    if (optionTop < list.scrollTop) {
      list.scrollTop = optionTop - ACTIVE_OPTION_SCROLL_MARGIN;
    } else if (optionBottom > list.scrollTop + list.clientHeight) {
      list.scrollTop =
        optionBottom - list.clientHeight + ACTIVE_OPTION_SCROLL_MARGIN;
    }
  }, [currentIndex, menuMounted]);

  // Position the portaled popup against the field, flipping upward when there
  // isn't room below, and keep it attached while any ancestor scrolls. The menu
  // is hosted in the nearest fixed-containing-block ancestor (a transformed
  // modal, etc.) or <body>, with coordinates measured relative to that host.
  useLayoutEffect(() => {
    if (!open) return;
    const containingBlock = findFixedContainingBlock(fieldRef.current);
    setPortalHost(containingBlock ?? document.body);

    function positionMenu() {
      const field = fieldRef.current;
      if (!field) return;
      const rect = field.getBoundingClientRect();
      const host = containingBlock?.getBoundingClientRect();
      const originLeft = host ? host.left : 0;
      const originTop = host ? host.top : 0;
      const hostHeight = host ? host.height : window.innerHeight;

      const fieldTop = rect.top - originTop;
      const fieldBottom = rect.bottom - originTop;
      const spaceBelow = hostHeight - fieldBottom;
      const spaceAbove = fieldTop;
      const openUpward =
        spaceBelow < MENU_MAX_HEIGHT + MENU_GAP && spaceAbove > spaceBelow;
      const available =
        (openUpward ? spaceAbove : spaceBelow) -
        MENU_GAP -
        MENU_VIEWPORT_MARGIN;

      setMenuPosition({
        left: rect.left - originLeft,
        width: rect.width,
        maxHeight: Math.max(0, Math.min(MENU_MAX_HEIGHT, available)),
        ...(openUpward
          ? { bottom: hostHeight - fieldTop + MENU_GAP }
          : { top: fieldBottom + MENU_GAP }),
      });
    }

    positionMenu();
    window.addEventListener("scroll", positionMenu, true);
    window.addEventListener("resize", positionMenu);
    return () => {
      window.removeEventListener("scroll", positionMenu, true);
      window.removeEventListener("resize", positionMenu);
    };
  }, [open]);

  function closeMenu() {
    setOpen(false);
    clearQuery();
  }

  useOutsideClick([rootRef, menuRef], closeMenu, open);

  function focusInputQuietly() {
    if (document.activeElement === searchRef.current) return;
    suppressOpenOnFocusRef.current = true;
    searchRef.current?.focus();
    suppressOpenOnFocusRef.current = false;
  }

  function commit(nextSelection: Set<string>) {
    onChange([...nextSelection]);
  }

  function committedIndex() {
    return navIndexByValue[value[0]] ?? 0;
  }

  function markMoved() {
    activeMovedRef.current = true;
    activeActedRef.current = false;
  }

  function moveActive(index: number) {
    markMoved();
    setActiveIndex(index);
  }

  function hoverActivate(event: React.MouseEvent, index: number) {
    const previous = lastPointerRef.current;
    lastPointerRef.current = { x: event.clientX, y: event.clientY };
    const pointerTravelled =
      !!previous &&
      (previous.x !== event.clientX || previous.y !== event.clientY);
    if (pointerTravelled && index !== currentIndex) moveActive(index);
  }

  function beginOpen(initialActiveIndex?: number, userNavigated = false) {
    resetNavigation(initialActiveIndex ?? committedIndex(), userNavigated);
    setOpen(true);
  }

  function openMenu(initialActiveIndex?: number, userNavigated = false) {
    if (disabled) return;
    if (!open) beginOpen(initialActiveIndex, userNavigated);
    searchRef.current?.focus();
  }

  function findAutofilledOption(text: string) {
    const needle = text.trim().toLowerCase();
    if (!needle) return undefined;
    return selectableOptions.find(
      (option) =>
        option.label.toLowerCase() === needle ||
        option.value.toLowerCase() === needle,
    );
  }

  function toggleOption(optionValue: string) {
    if (optionByValue[optionValue]?.disabled) return;
    activeActedRef.current = true;
    if (multiple) {
      const nextSelection = new Set(selectedValues);
      if (nextSelection.has(optionValue)) nextSelection.delete(optionValue);
      else nextSelection.add(optionValue);
      commit(nextSelection);
      searchRef.current?.focus();
    } else {
      commit(new Set([optionValue]));
      setOpen(false);
      clearQuery();
    }
  }

  function removeSelection(optionValue: string) {
    const nextSelection = new Set(selectedValues);
    nextSelection.delete(optionValue);
    commit(nextSelection);
    announce(
      strings.optionRemoved(optionByValue[optionValue]?.label ?? optionValue),
    );
  }

  function removeChip(optionValue: string, chipIndex: number) {
    const removeButtons = Array.from(
      rootRef.current?.querySelectorAll<HTMLElement>("[data-chip-remove]") ??
        [],
    );
    const neighbour =
      removeButtons[chipIndex + 1] ?? removeButtons[chipIndex - 1];
    if (neighbour) neighbour.focus();
    else focusInputQuietly();
    removeSelection(optionValue);
  }

  function clearSelection(event?: React.MouseEvent) {
    event?.stopPropagation();
    commit(new Set());
    clearQuery();
    setChipsExpanded(false);
    announce(strings.selectionCleared);
    focusInputQuietly();
  }

  function selectAllOptions() {
    const nextSelection = new Set(selectedValues);
    selectableOptions.forEach((option) => nextSelection.add(option.value));
    commit(nextSelection);
    announce(strings.selectedCount(nextSelection.size));
    searchRef.current?.focus();
  }

  function createOptionFromQuery() {
    const createdLabel = query.trim();
    if (!createdLabel) return;
    const createdValue = `${CREATED_VALUE_PREFIX}${createdLabel.toLowerCase()}`;
    if (!optionByValue[createdValue]) {
      setCreatedOptions((previous) => [
        ...previous,
        { value: createdValue, label: createdLabel },
      ]);
    }
    onCreate?.(createdLabel);
    announce(strings.created(createdLabel));
    activeActedRef.current = true;
    if (multiple) {
      commit(new Set(selectedValues).add(createdValue));
      clearQuery();
      searchRef.current?.focus();
    } else {
      commit(new Set([createdValue]));
      setOpen(false);
      clearQuery();
    }
  }

  function resolveActiveTarget(): ComboboxOption | "create" | null {
    if (currentIndex < 0) return null;
    if (currentIndex < navigableOptions.length)
      return navigableOptions[currentIndex];
    return showCreateRow ? "create" : null;
  }

  function commitActive({ addOnly }: { addOnly: boolean }) {
    const target = resolveActiveTarget();
    if (target === "create") createOptionFromQuery();
    else if (
      target &&
      !(addOnly && selectedValues.has(target.value)) &&
      !(
        !multiple &&
        selectedValues.has(target.value) &&
        target.value === value[0]
      )
    )
      toggleOption(target.value);
  }

  function activateTypeaheadMatch(character: string) {
    window.clearTimeout(typeaheadTimerRef.current);
    typeaheadTimerRef.current = window.setTimeout(() => {
      typeaheadBufferRef.current = "";
    }, TYPEAHEAD_RESET_MS);
    typeaheadBufferRef.current += character.toLocaleLowerCase();
    const buffer = typeaheadBufferRef.current;
    const repeatsOneCharacter = [...buffer].every(
      (typed) => typed === buffer[0],
    );
    const needle = repeatsOneCharacter ? buffer[0] : buffer;
    const current = open ? currentIndex : committedIndex();
    const searchStart = needle.length === 1 ? current + 1 : current;
    const count = navigableOptions.length;
    for (let offset = 0; offset < count; offset += 1) {
      const index = (Math.max(searchStart, 0) + offset) % count;
      if (
        navigableOptions[index].label.toLocaleLowerCase().startsWith(needle)
      ) {
        if (open) moveActive(index);
        else openMenu(index, true);
        return;
      }
    }
  }

  function commitIfNavigated() {
    if (
      !searchable &&
      !loading &&
      activeMovedRef.current &&
      !activeActedRef.current
    )
      commitActive({ addOnly: multiple });
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (disabled || event.nativeEvent.isComposing || event.keyCode === 229)
      return;
    if (event.key === "Tab" && open) {
      commitIfNavigated();
      flushSync(closeMenu);
    }
    const lastIndex = navigableCount - 1;
    const hasModifier = event.ctrlKey || event.metaKey;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (!open) return openMenu(event.altKey ? NO_ACTIVE_OPTION : undefined);
      if (event.altKey) return;
      markMoved();
      setActiveIndex(Math.min(currentIndex + 1, lastIndex));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      if (!open) return openMenu(event.altKey ? NO_ACTIVE_OPTION : lastIndex);
      if (event.altKey) {
        commitIfNavigated();
        return closeMenu();
      }
      markMoved();
      setActiveIndex(Math.max(currentIndex - 1, 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (!open) return openMenu();
      if (loading) return;
      commitActive({ addOnly: false });
    } else if (
      multiple &&
      open &&
      !query &&
      hasModifier &&
      event.key.toLowerCase() === "a"
    ) {
      event.preventDefault();
      selectAllOptions();
    } else if ((event.key === "Home" || event.key === "End") && !searchable) {
      event.preventDefault();
      if (loading) return;
      const targetIndex = event.key === "Home" ? 0 : lastIndex;
      if (open) moveActive(targetIndex);
      else openMenu(targetIndex, true);
    } else if (
      !searchable &&
      !loading &&
      event.key === " " &&
      !typeaheadBufferRef.current
    ) {
      event.preventDefault();
      if (!open) return openMenu();
      if (activeMovedRef.current && currentIndex >= 0)
        commitActive({ addOnly: false });
      else closeMenu();
    } else if (
      !searchable &&
      !loading &&
      event.key.length === 1 &&
      !event.altKey &&
      !hasModifier
    ) {
      event.preventDefault();
      activateTypeaheadMatch(event.key);
    } else if (
      event.key === "Backspace" &&
      !query &&
      multiple &&
      selectedValues.size > 0
    ) {
      removeSelection([...selectedValues][selectedValues.size - 1]);
    }
  }

  const resolvedPlaceholder =
    placeholder ?? (multiple ? "Select options" : "Select an option");
  const searchingNow = searchable && open;

  function inputPlaceholderText(): string {
    if (multiple)
      return searchingNow ? strings.searchPlaceholder : resolvedPlaceholder;
    return singleSelectedOption?.label ?? resolvedPlaceholder;
  }

  const inputValue = searchingNow ? query : (singleSelectedOption?.label ?? "");

  const optionDomId = (navIndex: number) => `${listboxId}-option-${navIndex}`;
  const createRowDomId = `${listboxId}-create`;
  const activeDescendantId =
    !menuMounted || loading
      ? undefined
      : showCreateRow && currentIndex === createRowIndex
        ? createRowDomId
        : navigableOptions[currentIndex]
          ? optionDomId(currentIndex)
          : undefined;
  const listboxLabel = typeof label === "string" ? label : undefined;

  const menu = (
    <div
      ref={menuRef}
      style={{
        position: "fixed",
        left: menuPosition?.left,
        width: menuPosition?.width,
        maxHeight: menuPosition?.maxHeight,
        top: menuPosition?.top,
        bottom: menuPosition?.bottom,
      }}
      className="z-[1000] flex flex-col overflow-hidden rounded-lg border border-divider bg-page shadow-lg font-sans text-sm"
    >
      {multiple && !loading && (
        <ComboboxToolbar
          allSelected={allOptionsSelected}
          selectedCount={selectedValues.size}
          strings={strings}
          onSelectAll={selectAllOptions}
          onClear={() => clearSelection()}
        />
      )}

      <div
        ref={listRef}
        id={listboxId}
        role="listbox"
        aria-multiselectable={multiple || undefined}
        aria-label={listboxLabel}
        aria-busy={loading || undefined}
        className="relative min-h-0 flex-1 overflow-y-auto p-1"
      >
        {loading && (
          <div className="flex items-center justify-center gap-2 py-[18px] text-[0.8125rem] text-muted">
            <Spinner />
            {strings.loading}
          </div>
        )}

        {!loading && matchingGroups.length === 0 && !showCreateRow && (
          <div className="px-2 pt-5 pb-2 text-center text-sm text-muted">
            {strings.empty}
          </div>
        )}

        {!loading &&
          matchingGroups.map((group, groupIndex) => (
            <div
              key={group.group ?? `group-${groupIndex}`}
              role={group.group ? "group" : undefined}
              aria-label={group.group}
              className="[&+&]:mt-0.5"
            >
              {group.group && (
                <div className="px-2 pt-2 pb-0.5 text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">
                  {group.group}
                </div>
              )}
              {group.items.map((option) => {
                const navIndex = navIndexByValue[option.value];
                return (
                  <ComboboxOptionRow
                    key={option.value}
                    id={option.disabled ? undefined : optionDomId(navIndex)}
                    option={option}
                    multiple={multiple}
                    selected={selectedValues.has(option.value)}
                    active={!option.disabled && navIndex === currentIndex}
                    navIndex={navIndex}
                    unavailableLabel={strings.unavailable}
                    onActivate={(event) => hoverActivate(event, navIndex)}
                    onSelect={() => toggleOption(option.value)}
                  />
                );
              })}
            </div>
          ))}

        {!loading && showCreateRow && (
          <ComboboxCreateRow
            id={createRowDomId}
            query={query.trim()}
            active={createRowIndex === currentIndex}
            standalone={matchingGroups.length === 0}
            navIndex={createRowIndex}
            prefix={strings.create}
            accessibleName={strings.createOptionName(query.trim())}
            onActivate={(event) => hoverActivate(event, createRowIndex)}
            onCreate={createOptionFromQuery}
          />
        )}
      </div>
    </div>
  );

  function belongsToControl(node: Node | null) {
    if (!node) return false;
    if (fieldRef.current?.contains(node) || menuRef.current?.contains(node))
      return true;
    return (
      node instanceof Element &&
      !!rootRef.current?.contains(node) &&
      !!node.closest("[data-combobox-chips]")
    );
  }

  function handleBlur(event: React.FocusEvent) {
    if (!belongsToControl(event.target)) return;
    const nextFocused = event.relatedTarget as Node | null;
    if (nextFocused && menuRef.current?.contains(nextFocused)) return;
    const staysInControl = belongsToControl(nextFocused);
    if (event.target === searchRef.current || !staysInControl) closeMenu();
    if (!staysInControl) onBlur?.();
  }

  return (
    <div ref={rootRef} onBlur={handleBlur} className="w-full font-sans text-sm">
      <FieldLabel
        htmlFor={inputId}
        label={label}
        required={required}
        info={info}
        infoOpen={infoOpen}
        labelActions={labelActions}
        labelHidden={labelHidden}
      />

      <div
        ref={fieldRef}
        onClick={() => openMenu()}
        className={classNames(
          "flex items-center min-h-target pl-3 pr-1.5 rounded border transition-[border-color,box-shadow,background]",
          {
            "bg-page": !disabled,
            "bg-surface cursor-not-allowed": disabled,
            "cursor-text": !disabled && searchable,
            "cursor-pointer": !disabled && !searchable,
            "border-default": !open && !hasError,
            "border-primary ring-2 ring-focus": open && !hasError,
            "border-danger ring-2 ring-danger": hasError,
          },
        )}
      >
        <input
          ref={assignRef}
          id={inputId}
          name={name}
          autoComplete={autoComplete}
          className={classNames(
            "flex-1 min-w-0 h-[42px] border-0 outline-none bg-transparent text-sm text-default text-ellipsis disabled:cursor-not-allowed",
            searchable ? "cursor-[inherit]" : "cursor-pointer",
            searchingNow && singleSelectedOption
              ? "placeholder:text-default"
              : "placeholder:text-muted",
          )}
          value={inputValue}
          placeholder={inputPlaceholderText()}
          disabled={disabled}
          readOnly={!searchable}
          onChange={(event) => {
            if (!searchable) return;
            if (!(event.nativeEvent instanceof InputEvent)) {
              const autofilled = findAutofilledOption(event.target.value);
              if (autofilled) {
                if (!multiple || !selectedValues.has(autofilled.value)) {
                  toggleOption(autofilled.value);
                }
                return;
              }
            }
            const committedLabel = !searchingNow
              ? singleSelectedOption?.label
              : undefined;
            const typedQuery =
              committedLabel && event.target.value.startsWith(committedLabel)
                ? event.target.value.slice(committedLabel.length)
                : event.target.value;
            setQuery(typedQuery);
            resetNavigation(0);
            setOpen(true);
            onSearchChange?.(typedQuery);
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (!disabled && !suppressOpenOnFocusRef.current && !open)
              beginOpen();
          }}
          role="combobox"
          aria-expanded={open}
          aria-controls={menuMounted ? listboxId : undefined}
          aria-activedescendant={activeDescendantId}
          aria-autocomplete={searchable ? "list" : "none"}
          aria-invalid={hasError}
        />
        <div className="flex items-center gap-0.5 shrink-0">
          {loading && <Spinner className="mx-[5px]" />}
          {!loading && clearable && selectedValues.size > 0 && !disabled && (
            <button
              type="button"
              className="inline-flex items-center justify-center w-target h-target -my-px p-0 border-0 bg-transparent rounded text-muted cursor-pointer transition-colors hover:bg-surface hover:text-default"
              onMouseDown={keepSearchInputFocused}
              onClick={clearSelection}
              aria-label={multiple ? strings.clearAll : strings.clear}
              title={strings.clear}
            >
              <X size={15} />
            </button>
          )}
          <span className="w-px h-4 bg-raised mx-0.5" />
          <span
            className={classNames(
              "inline-flex items-center justify-center w-target h-target -my-px text-muted transition-transform",
              disabled ? "pointer-events-none" : "cursor-pointer",
              open && "rotate-180",
            )}
            onMouseDown={keepSearchInputFocused}
            onClick={(event) => {
              event.stopPropagation();
              if (disabled) return;
              if (open) closeMenu();
              else beginOpen();
              searchRef.current?.focus();
            }}
            aria-hidden="true"
          >
            <ChevronDown size={16} strokeWidth={1.75} />
          </span>
        </div>
      </div>

      {menuMounted && createPortal(menu, portalHost)}

      {multiple && selectedOptions.length > 0 && (
        <ComboboxChips
          options={selectedOptions}
          maxChips={maxChips}
          expanded={chipsExpanded}
          disabled={disabled}
          strings={strings}
          onRemove={removeChip}
          onExpand={() => setChipsExpanded(true)}
          onCollapse={() => setChipsExpanded(false)}
        />
      )}

      <span
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        <span key={announcement.id}>{announcement.text}</span>
      </span>

      <FieldMessage
        error={error}
        valid={valid}
        hint={hint}
        isEmpty={selectedOptions.length === 0}
      />
    </div>
  );
}

export default Combobox;
